import { useCallback, useRef, useState } from 'react';
import { Receipt, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { deferaZeroUrl, deferaCapitalUrl, deferaCapitalNmiUrl } from '@/lib/apiUrls';
import { buildTripDocumentData, airlineLogoProxyUrl } from '@/utils/tripDocument';
import { renderTripDocumentHtml } from '@/utils/tripDocumentHtml';
import { buildTripStatementPdfBlob } from '@/utils/tripDocumentPdf';
import { publicOrigin } from '@/lib/publicOrigin';

/**
 * Download the customer's trip as a PDF ("Trip Statement").
 *
 * Rendered as a compact receipt-icon outline button so it slots into the
 * existing action bar without competing with the primary CTA row.
 *
 * On click:
 *   1. Fetches the live payment schedule + history from the appropriate
 *      Defera Zero / Defera Capital / NMI edge function so the PDF matches
 *      what the customer sees in-app. Slice Pay / installments fall back
 *      to the booking's persisted `payment_schedule`.
 *   2. Fetches each airline logo, rasterizes Duffel's SVGs to PNG data
 *      URIs (jsPDF only embeds raster images), and inlines the Defera logo.
 *   3. Builds a NATIVE vector PDF via jsPDF drawing primitives
 *      (tripDocumentPdf.js) — real selectable text, crisp at any zoom,
 *      ~30KB file, clean row-boundary pagination. No html2canvas, no
 *      rasterized text. jspdf/jspdf-autotable load via dynamic import so
 *      their weight ships only on first click.
 *   4. Delivers the Blob through Web Share (mobile/PWA) or <a download>
 *      (desktop). If the PDF build fails entirely, desktop falls back to
 *      the styled-HTML print window.
 */
export default function DownloadTripDocumentButton({
  trip,
  passengers,
  currentUser,
  tripUser,
  route,
  variant = 'icon',
  className,
}) {
  const [isBusy, setIsBusy] = useState(false);
  const busyRef = useRef(false);

  const handleClick = useCallback(async () => {
    if (busyRef.current) return;
    if (!trip || (Array.isArray(trip) && trip.length === 0)) {
      toast.error('Trip details are still loading. Please try again in a moment.');
      return;
    }
    busyRef.current = true;
    setIsBusy(true);
    try {
      const mainFlight = Array.isArray(trip) ? trip[0] : trip;
      const scheduleData = await fetchScheduleData(mainFlight);
      const logoPath = '/defera-print-logo.png';
      const inlineLogoDataUri = await loadImageDataUri(logoPath);
      const absoluteLogoUrl = new URL(logoPath, publicOrigin()).toString();

      const documentData = buildTripDocumentData({
        trip,
        passengers,
        currentUser,
        tripUser,
        scheduleData,
        route,
      });

      await inlineSegmentLogos(documentData.segments);

      // Filename is the booking reference — short, recognizable, and the
      // same identifier the customer quotes to support. The long internal
      // document number stays inside the PDF header only.
      const filename = `${documentData.trip.reference || documentData.document.number || 'trip-statement'}.pdf`;
      let deliveryOutcome = null;
      try {
        const blob = await buildTripStatementPdfBlob(documentData, {
          logoDataUri: inlineLogoDataUri,
        });
        deliveryOutcome = await deliverBlob(blob, filename);
      } catch (err) {
        console.warn('Native PDF generation failed; attempting print-window fallback', err);
      }
      if (deliveryOutcome === 'shared') {
        // Web Share sheet closed successfully. iOS puts the PDF right in
        // the share targets ("Save to Files", "Mail", "Messages", etc.);
        // no additional toast needed.
      } else if (deliveryOutcome === 'downloaded') {
        toast.success('Trip statement downloaded');
      } else if (deliveryOutcome === 'opened') {
        toast.success('Trip statement opened — use your browser to save it');
      } else if (deliveryOutcome === 'failed') {
        toast.error("We couldn't finish saving the PDF. Check Files — it may already be there.");
      } else if (deliveryOutcome !== 'cancelled') {
        // PDF pipeline failed entirely. Fall back to the popup + print
        // path on desktop. Mobile/PWA can't reliably open a popup, so we
        // surface a toast instead of blanking the screen.
        if (canOpenPrintWindowHere()) {
          const html = renderTripDocumentHtml(documentData, {
            inlineLogoDataUri,
            fallbackLogoUrl: absoluteLogoUrl,
          });
          openPrintWindow(html, documentData.document.number);
        } else {
          toast.error("We couldn't build the PDF on this device. Please try again from a desktop browser.");
        }
      }
    } catch (err) {
      console.error('Failed to build trip statement PDF', err);
      toast.error('We could not build your trip statement. Please try again.');
    } finally {
      busyRef.current = false;
      setIsBusy(false);
    }
  }, [trip, passengers, currentUser, tripUser, route]);

  if (variant === 'text') {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={isBusy}
        className={cn(
          'bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20',
          className,
        )}
        aria-label="Download trip statement PDF"
      >
        {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
        <span>{isBusy ? 'Preparing…' : 'Trip statement'}</span>
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      size="icon"
      onClick={handleClick}
      disabled={isBusy}
      className={cn(
        'bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-10 w-10',
        className,
      )}
      title="Download trip statement PDF"
      aria-label="Download trip statement PDF"
    >
      {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
    </Button>
  );
}

/**
 * Look up the live schedule + payment history for Defera Zero / Defera
 * Capital (Stripe + NMI). Returns `null` for Slice Pay / legacy trips or
 * whenever the call fails — the renderer falls back gracefully to the
 * persisted `flight_data.payment_schedule` in that case.
 */
async function fetchScheduleData(mainFlight) {
  if (!mainFlight) return null;
  const fd = normalizeFlightData(mainFlight?.flightData || mainFlight?.flight_data);
  const method = fd._payment_method || fd.payment_method || mainFlight?.payment_method;
  const isDeferaCapital = method === 'defera_capital';
  const isDeferaZero = method === 'defera_zero';
  if (!isDeferaCapital && !isDeferaZero) return null;
  const isNmi = isDeferaCapital && fd._payment_processor === 'nmi';
  const tripId = mainFlight?.id || mainFlight?.bookingId;
  if (!tripId) return null;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return null;
    const url = isNmi
      ? deferaCapitalNmiUrl('schedule')
      : (isDeferaCapital ? deferaCapitalUrl('schedule') : deferaZeroUrl('schedule'));
    const res = await fetch(`${url}?booking_id=${encodeURIComponent(tripId)}`, {
      headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` }),
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    return data || null;
  } catch {
    return null;
  }
}

function normalizeFlightData(raw) {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) || {}; } catch { return {}; }
  }
  return typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
}

/**
 * Fetch `url` and return a base64 data-URI. Fails soft: on any error we
 * return null and the printable falls back to the caller-provided src,
 * which usually still resolves inside a same-origin popup.
 */
async function loadImageDataUri(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { credentials: 'omit', mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await blobToDataUri(blob);
  } catch {
    return null;
  }
}

function blobToDataUri(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

/**
 * Pre-fetch every unique airline logo URL in the itinerary and attach
 * render-ready assets on each segment:
 *   - `marketingCarrierLogoPng` — PNG data URI for jsPDF `addImage`
 *     (the ONLY format jsPDF reliably embeds; SVG data URIs do NOT work).
 *   - `marketingCarrierLogoSvg` — raw SVG markup (kept for debugging /
 *     future vector attempts; not used on the critical PDF path).
 *   - `marketingCarrierLogoUrl` — SVG or PNG data URI for the HTML print
 *     fallback's `<img src>`.
 *
 * Duffel serves SVGs. We fetch the markup, rasterize it to PNG via an
 * offscreen canvas (using a blob: URL for the Image src — more reliable
 * on iOS Safari than data: URIs), and store the PNG. If the fetch fails
 * we fall back to loading the URL directly through a crossOrigin Image.
 *
 * Mutating the shared segments array is intentional.
 */
async function inlineSegmentLogos(segments) {
  if (!Array.isArray(segments) || segments.length === 0) return;
  const cache = new Map();
  await Promise.all(segments.map(async (seg) => {
    // Try sources in reliability order: same-origin PNG proxy first
    // (no canvas, works on iOS PWA), then persisted/CDN SVG URLs.
    const candidates = [
      airlineLogoProxyUrl(seg?.marketingCarrierCode),
      seg?.marketingCarrierLogoUrl || '',
    ].filter((url, idx, arr) => url && !url.startsWith('data:') && arr.indexOf(url) === idx);

    let assets = null;
    for (const url of candidates) {
      if (!cache.has(url)) {
        cache.set(url, loadAirlineLogoAssets(url));
      }
      assets = await cache.get(url);
      if (assets?.pngDataUri) break;
    }

    seg.marketingCarrierLogoPng = assets?.pngDataUri || '';
    seg.marketingCarrierLogoSvg = assets?.svgMarkup || '';
    seg.marketingCarrierLogoUrl = assets?.htmlDataUri || assets?.pngDataUri || '';
  }));
}

/**
 * Fetch an airline logo and return a PNG data URI for PDF embedding.
 * PNG URLs (especially our same-origin /api/airline-logo proxy) are
 * fetched as raw bytes — no canvas step. SVG URLs go through canvas
 * rasterization as a last resort.
 */
async function loadAirlineLogoAssets(url) {
  if (url.toLowerCase().includes('.png')) {
    const pngDataUri = await fetchPngDataUri(url);
    if (pngDataUri) {
      return { pngDataUri, svgMarkup: '', htmlDataUri: pngDataUri };
    }
  }

  try {
    const res = await fetch(url, { credentials: 'omit', mode: 'cors' });
    if (res.ok) {
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('png')) {
        const pngDataUri = await fetchPngDataUri(url);
        if (pngDataUri) {
          return { pngDataUri, svgMarkup: '', htmlDataUri: pngDataUri };
        }
      }
      const isSvg = contentType.includes('svg') || url.toLowerCase().endsWith('.svg');
      if (isSvg) {
        const text = await res.text();
        if (text.includes('<svg')) {
          const pngDataUri = await rasterizeSvgMarkupToPng(text, 128);
          if (pngDataUri) {
            return {
              pngDataUri,
              svgMarkup: text,
              htmlDataUri: `data:image/svg+xml;base64,${toBase64Utf8(text)}`,
            };
          }
        }
      } else {
        const blob = await res.blob();
        const dataUri = await blobToDataUri(blob);
        if (dataUri?.startsWith('data:image/png') || dataUri?.startsWith('data:image/jpeg')) {
          return { pngDataUri: dataUri, svgMarkup: '', htmlDataUri: dataUri };
        }
      }
    }
  } catch {
    // fall through
  }

  const pngDataUri = await rasterizeImageUrlToPng(url, 128);
  return pngDataUri ? { pngDataUri, svgMarkup: '', htmlDataUri: pngDataUri } : null;
}

/** Fetch a PNG and return a data URI — no canvas involved. */
async function fetchPngDataUri(url) {
  try {
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.includes('png') && !url.toLowerCase().includes('.png')) return null;
    const dataUri = await blobToDataUri(blob);
    return dataUri?.startsWith('data:image/png') ? dataUri : null;
  } catch {
    return null;
  }
}

/**
 * Rasterize SVG markup to a square PNG data URI. Uses a blob: object URL
 * for the Image src rather than a data: URI — iOS Safari handles blob
 * URLs more reliably for SVG → canvas export.
 */
function rasterizeSvgMarkupToPng(svgMarkup, size = 128) {
  return new Promise((resolve) => {
    let objectUrl = '';
    try {
      const blob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
      objectUrl = URL.createObjectURL(blob);
    } catch {
      resolve(null);
      return;
    }
    rasterizeImageUrlToPng(objectUrl, size).then((png) => {
      try { URL.revokeObjectURL(objectUrl); } catch { /* ignore */ }
      resolve(png);
    });
  });
}

/**
 * Load any CORS-allowed image URL into an offscreen canvas and return a
 * PNG data URI. Accepts http(s) URLs and blob: URLs.
 */
function rasterizeImageUrlToPng(url, size = 128) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const timer = setTimeout(() => resolve(null), 12_000);
    img.onload = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        const iw = img.naturalWidth || img.width || size;
        const ih = img.naturalHeight || img.height || size;
        const scale = Math.min(size / iw, size / ih);
        const w = iw * scale;
        const h = ih * scale;
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    img.src = url;
  });
}

/** Base64-encode a UTF-8 string (btoa alone chokes on non-Latin1 chars). */
function toBase64Utf8(str) {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary);
}

/**
 * Cross-platform Blob delivery. Prefer navigator.share on mobile/PWA
 * (avoids the blank-screen behavior we saw when jsPDF's default save()
 * routed through a blob URL that PWA standalone mode couldn't render).
 * Fall back to <a download> on desktop, and to a new-tab blob URL as
 * an absolute last resort.
 */
async function deliverBlob(blob, filename) {
  const file = typeof File !== 'undefined'
    ? new File([blob], filename, { type: 'application/pdf', lastModified: Date.now() })
    : null;
  const mobile = isProbablyMobile();

  // Path 1: Web Share API with file support. iOS Safari + PWA get the
  // native share sheet ("Save to Files" is one tap away). Android Chrome
  // gets the standard share sheet. Desktop Chrome/Edge support this too
  // as of 2023+, but we still prefer <a download> there so customers
  // get a plain file download without an extra share picker.
  if (
    file
    && mobile
    && typeof navigator !== 'undefined'
    && typeof navigator.share === 'function'
    && typeof navigator.canShare === 'function'
    && navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({
        files: [file],
        title: filename,
      });
      return 'shared';
    } catch (err) {
      // User cancelled the share sheet — treat as a successful no-op
      // (they saw the sheet, they chose not to save). AbortError is
      // Safari's cancel signal.
      if (err?.name === 'AbortError') return 'cancelled';
      console.warn('navigator.share failed on mobile', err);
      // Do NOT fall through to <a download> on mobile. iOS often already
      // persisted the file via the share sheet before the promise rejects,
      // and anchor download would create a duplicate in Files.
      return 'failed';
    }
  }

  // Path 2: <a download> anchor click — works on every desktop browser
  // and on Android Chrome when Web Share isn't available. iOS Safari
  // ignores the download attribute (that's why we tried Web Share first).
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Defer revocation slightly so the browser can start the download
    // before the URL disappears.
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
    return 'downloaded';
  } catch {
    // Path 3: last-ditch — open the blob URL in a new tab and let the
    // customer save it manually. Only useful on ancient browsers with no
    // Web Share and where anchor clicks are blocked.
    try {
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      return 'opened';
    } catch {
      URL.revokeObjectURL(url);
      throw new Error('unable to deliver PDF blob');
    }
  }
}

/**
 * Heuristic: is the current environment likely a mobile browser or PWA?
 * We use this to prefer navigator.share over anchor download. Not a
 * security boundary — worst case we show a share sheet on a desktop
 * with a touchscreen, which is still a fine UX.
 */
function isProbablyMobile() {
  if (typeof window === 'undefined') return false;
  // PWA in standalone/fullscreen mode — Web Share is by far the best
  // path here because window.open renders a blank page.
  const displayModes = ['standalone', 'fullscreen', 'minimal-ui'];
  if (typeof window.matchMedia === 'function') {
    for (const mode of displayModes) {
      if (window.matchMedia(`(display-mode: ${mode})`).matches) return true;
    }
  }
  // iOS home-screen "add to home screen" flag.
  if (typeof window.navigator !== 'undefined' && window.navigator.standalone === true) return true;
  // High-fidelity UA-Client-Hints signal when available.
  if (window.navigator?.userAgentData?.mobile) return true;
  // Legacy UA sniff — matches iOS Safari, Android Chrome, iPadOS Safari
  // (which reports as Macintosh but has touch support).
  const ua = window.navigator?.userAgent || '';
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return true;
  const isMacTouch = /Macintosh/.test(ua)
    && typeof window.navigator?.maxTouchPoints === 'number'
    && window.navigator.maxTouchPoints > 1;
  return isMacTouch;
}

/**
 * The popup+window.print() fallback is only safe when we can open a new
 * window that will actually render — on mobile Safari + PWA standalone
 * mode, window.open('') just shows a blank tab. Same environments that
 * benefit from navigator.share are the ones where the print fallback
 * should be skipped.
 */
function canOpenPrintWindowHere() {
  return !isProbablyMobile();
}

/**
 * Fallback path: open the styled HTML in a new same-origin popup and
 * trigger `window.print()`. Only used when the native PDF builder throws
 * (very rare — mostly happens when a browser blocks canvas taint from a
 * cross-origin image that slipped past our inlining pass).
 */
function openPrintWindow(html, documentNumber) {
  const popup = window.open('', '_blank', 'noopener,noreferrer,width=900,height=1100');
  if (popup && !popup.closed) {
    try {
      popup.document.open();
      popup.document.write(html);
      popup.document.close();
      popup.document.title = documentNumber || 'Trip Statement';
      const trigger = () => {
        try {
          popup.focus();
          popup.print();
        } catch (err) {
          console.warn('Popup print failed', err);
        }
      };
      if (popup.document.readyState === 'complete') {
        setTimeout(trigger, 250);
      } else {
        popup.addEventListener('load', () => setTimeout(trigger, 250));
      }
      return;
    } catch (err) {
      console.warn('Popup write failed, falling back to iframe print', err);
      try { popup.close(); } catch { /* ignore */ }
    }
  }

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  document.body.appendChild(iframe);
  const trigger = () => {
    try {
      const win = iframe.contentWindow;
      if (!win) return;
      win.focus();
      win.print();
      setTimeout(() => {
        try { document.body.removeChild(iframe); } catch { /* ignore */ }
      }, 60_000);
    } catch (err) {
      console.warn('Iframe print failed', err);
    }
  };
  iframe.onload = () => setTimeout(trigger, 250);
  iframe.srcdoc = html;
}
